import { ChangeEvent } from 'react';
import { MenuItem, Select } from '@mui/material';
import clsx from 'clsx';
import { format, parseISO } from 'date-fns';
import { SELECT_DATE } from 'common/constants/inputs';
import { useLedgerContext } from 'context/LedgerContext';
import theme from 'theme';
import slashedToIsoFormat from 'utilities/slashedToIsoFormat';
import { PurchaseDateSelectProps } from './PurchaseDateSelect.props';

const PurchaseDateSelect = ({ cell, options }: PurchaseDateSelectProps) => {
  const { onCellsChange } = useLedgerContext();

  const handleChange = (event: ChangeEvent<{ value: unknown }>) => {
    const newValue = event.target.value?.toString();
    // find option with same value and convert it to iso format date
    const option = options.find(opt => opt.value === newValue);

    const date = option ? slashedToIsoFormat(option) : '';

    if (option?.value) {
      if (cell) {
        // eslint-disable-next-line no-param-reassign
        cell.acquisition_ref = option.value;
      }
    }
    if (cell) {
      onCellsChange([{ cell, value: date }]);
    }
  };

  const renderFormattedDate = (value: string) => {
    try {
      // Assuming the value is in 'YYYY-MM-DD' format
      return format(parseISO(value), 'MM/dd/yyyy');
    } catch {
      return '';
    }
  };

  return (
    <Select
      disabled={Boolean(cell?.readOnly)}
      disableUnderline
      MenuProps={{
        disablePortal: true,
        PaperProps: {
          sx: {
            borderRadius: 0,
            boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.1), 0 4px 11px rgba(0, 0, 0, 0.1)',
            maxHeight: theme.spacing(30),
            ml: -3.5,
            mt: -3.875,
            width: theme.spacing(22.875),
          },
        },
      }}
      onChange={handleChange}
      renderValue={value => renderFormattedDate(value as string)}
      sx={{
        '& .MuiList-root': {
          padding: 0,
        },
        '& .MuiSelect-select': {
          background: 'transparent',
          color: cell?.readOnly
            ? `${theme.palette.common.black} !important`
            : `${theme.palette.primary[700]} !important`,
          fontSize: theme.spacing(1.75),
          lineHeight: '1.2', // Better line spacing for wrapped text
          minHeight: 'auto',
          mr: 1,
          padding: theme.spacing(0.8125, 1.5),
          whiteSpace: 'nowrap',
        },
        '& .MuiSelect-select.Mui-disabled': {
          WebkitTextFillColor: theme.palette.common.black,
        },
        '& .MuiSelect-icon': {
          display: cell?.readOnly ? 'none' : 'block',
          fill: theme.palette.primary[600],
        },
      }}
      value={cell?.value ?? SELECT_DATE}
      variant="standard">
      {options.map(option => (
        <MenuItem
          className={clsx({
            'Mui-selected': option.label === renderFormattedDate(cell?.value as string),
          })}
          key={option.value}
          sx={{
            '&.MuiMenuItem-root': {
              color: theme.palette.primary[700],
              fontSize: theme.spacing(1.75),
              justifyContent: 'flex-end',
              lineHeight: '1.2', // Better line spacing for wrapped text
              minHeight: theme.spacing(2.5), // Ensure minimum height for readability
              overflow: 'visible',
              padding: theme.spacing(1, 1.5),
              textAlign: 'right',
              whiteSpace: 'normal', // Allow wrapping
              wordWrap: 'break-word', // Break long words if needed
            },

            '&:hover': {
              backgroundColor: '#DEEBFF',
            },

            '&.Mui-selected, &.Mui-selected:hover': {
              backgroundColor: '#2684FF',
              color: theme.palette.common.white,
            },
          }}
          value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
};

export default PurchaseDateSelect;
